import React, { memo, useCallback, useEffect, useState } from "react";

import { LoadingOutlined } from "@ant-design/icons";
import Select from "antd/es/select";
import Spin from "antd/es/spin";
import useBasemapStyleOptions from "component/map/utils/useBasemapStyleOptions";
import { Map } from "google-maps-react";
import { FormattedMessage, useIntl } from "react-intl";

import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";

import googleMapStyles from "../../../../component/map/googleMapStyles";
import TextWithLine from "../../../../component/typography/TextWithLine";

import DisplayMarkers from "./DisplayMarkers";

export const ReportMap = ({
  closestLocationForTime = undefined,
  mapLoading,
  onClick = undefined,
  pins,
  showingHeatMap = false,
  ...rest
}) => {
  const intl = useIntl();
  const [map, setMap] = useState();
  const [basemap, setBasemap] = useState("Default");
  const [activeMarkerProps, setActiveMarkerProps] = useState({});
  const [numUniquePinsOnMap, setNumUniquePinsOnMap] = useState(0);

  const antIcon = <LoadingOutlined spin style={{ fontSize: 24, marginTop: 200 }} />;
  // determine actively clicked marker
  let activeMarkerId = "";
  if (activeMarkerProps && activeMarkerProps.point) {
    activeMarkerId = activeMarkerProps.point.id;
  }

  const handleSetActiveMarkerProps = useCallback(
    (props) => {
      if (onClick && props.point) {
        onClick(props.point);
      }

      setActiveMarkerProps(props);
    },
    [setActiveMarkerProps, onClick]
  );

  useEffect(() => {
    // don't reset bounds if using user location slider
    let viewingUserLocation = false;
    if (pins.length && pins[pins.length - 1].objType === "user-location") {
      viewingUserLocation = true;
    }

    // pins changes ==> set new bounds, remove any open info windows
    if (map && !viewingUserLocation) {
      setBounds(map);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, map]);

  // initialize map upon load (traffic, bounds, heatmap, etc.)
  const initMap = (props, map) => {
    setMap(map);
    setBounds(map);
    setMapOptions(map);
  };

  // sets bounds for the map
  const setBounds = (map) => {
    if (map && pins.length) {
      const bounds = new window.google.maps.LatLngBounds();
      pins.forEach((pin) =>
        bounds.extend({
          lat: pin.geoPoint.coordinates[1],
          lng: pin.geoPoint.coordinates[0],
        })
      );
      map.fitBounds(bounds);
      if (pins.length < 2) {
        map.setZoom(12);
      }
    }
  };

  // set mapping options
  const setMapOptions = (map) => {
    map.setOptions({
      zoom: 12,
    });
  };

  // changes basemap
  const handleBasemapChange = (value) => {
    setBasemap(value);
    map.setOptions({
      styles: googleMapStyles[value],
    });
  };

  return (
    <div>
      {showingHeatMap ? (
        <TextWithLine>
          <FormattedMessage
            id="reports.map.header.revenueHeat"
            defaultMessage="Revenue Heat Map"
            description="Title for Revenue Heat Map on Overview Screen."
          />
        </TextWithLine>
      ) : (
        <TextWithLine className={styles.mapHeaderText}>
          <FormattedMessage
            id="reports.map.header.pins"
            defaultMessage="Pins Showing ({count})"
            description="Title for Pins in Pins Showing section on Last Location screen."
            values={{
              count: numUniquePinsOnMap,
            }}
          />
        </TextWithLine>
      )}

      {/* actual map, pins, and pop-up annotation */}
      <Map
        containerStyle={styles.mapsContainerStyle}
        google={window.google}
        loaded={!!window.google?.maps}
        onReady={initMap}
        styles={googleMapStyles[basemap]}
      >
        {/* map spinner */}
        <Spin indicator={antIcon} style={{ display: mapLoading ? "block" : "none" }} />

        {/* choose basemap */}
        <Select
          defaultValue={[BaseMapStyle.STANDARD]}
          onChange={handleBasemapChange}
          optionLabelProp="label"
          options={useBasemapStyleOptions(intl)}
          style={{ ...styles.basemapPicker, display: mapLoading ? "none" : "block" }}
        />

        {/* pins on map */}
        <MapPoints
          activeMarkerId={activeMarkerId}
          closestLocationForTime={closestLocationForTime}
          pins={pins}
          setActiveMarkerProps={handleSetActiveMarkerProps}
          setNumUniquePinsOnMap={setNumUniquePinsOnMap}
          showingHeatMap={showingHeatMap}
          {...rest}
        />
      </Map>
    </div>
  );
};

// returns pins on map (and prevents unnecessary re-rendering of map on pin clicks)
class MapPoints extends React.Component {
  shouldComponentUpdate(prevProps) {
    // pins or map were updated or user location has moved > re-render component
    if (
      this.props.pins !== prevProps.pins ||
      this.props.map !== prevProps.map ||
      this.props.closestLocationForTime !== prevProps.closestLocationForTime
    ) {
      return true;
    } else {
      return false;
    }
  }

  render() {
    return (
      <DisplayMarkers
        activeMarkerId={this.props.activeMarkerId}
        closestLocationForTime={this.props.closestLocationForTime}
        map={this.props.map}
        pins={this.props.pins}
        setActiveMarkerProps={this.props.setActiveMarkerProps}
        setNumUniquePinsOnMap={this.props.setNumUniquePinsOnMap}
        showingHeatMap={this.props.showingHeatMap}
      />
    );
  }
}

export default memo(ReportMap);

// =============== STYLES =============== //
const styles = {
  basemapPicker: {
    left: 10,
    position: "absolute",
    top: 60,
    width: 170,
  },
  mapHeaderText: {
    display: "flex",
  },
  mapsContainerStyle: {
    float: "left",
    height: "450px",
    position: "relative",
    width: "100%",
  },
  mapsLoadingContainer: {
    marginLeft: 30,
  },
};
